import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * State for the Blueticks side-panel injected into WhatsApp Web.
 *
 * The panel mirrors the layout of the WA Web Plus extension that the
 * user referenced: a right-edge drawer the width of the chats list,
 * with a header (close + title), an empty content area for menu items
 * (filled in a follow-up), and a footer with the user's subscription
 * info. A `view` field drives a simple breadcrumb-style sub-page
 * navigation — "home" shows the top-level menu, anything else is a
 * sub-screen with a "back" affordance in the header.
 *
 * The toolbar action click (background.ts) toggles `open` via the
 * `openBlueticksPanel` action shipped through the existing content-
 * script message channel.
 */
export interface BlueticksPanelState {
  open: boolean;
  /** Free-form view id. 'home' is the top-level menu; anything else
   *  is a sub-page rendered with a back button in the header. The
   *  component decides which sub-page each id maps to. */
  view: string;
  /** Navigation history so the back button always returns to whichever
   *  view the user came from. The current `view` is at the top of the
   *  stack — popping it returns to the previous entry. */
  history: string[];
}

const initialState: BlueticksPanelState = {
  open: false,
  view: 'home',
  history: [],
};

const blueticksPanelSlice = createSlice({
  name: 'BlueticksPanel',
  initialState,
  reducers: {
    openPanel(state) {
      state.open = true;
    },
    closePanel(state) {
      state.open = false;
      // Reset navigation when the user closes the panel — next open
      // starts at the top-level menu instead of dropping them back
      // into wherever they left off mid-sub-page.
      state.view = 'home';
      state.history = [];
    },
    togglePanel(state) {
      state.open = !state.open;
      if (!state.open) {
        state.view = 'home';
        state.history = [];
      }
    },
    /** Push a new sub-view onto the nav stack. */
    navigateTo(state, action: PayloadAction<string>) {
      if (action.payload && action.payload !== state.view) {
        state.history.push(state.view);
        state.view = action.payload;
      }
    },
    /** Pop the nav stack; no-op if already at the root. */
    navigateBack(state) {
      const prev = state.history.pop();
      if (prev) state.view = prev;
      else state.view = 'home';
    },
    /** Jump straight to the top-level menu without unwinding history. */
    resetToHome(state) {
      state.view = 'home';
      state.history = [];
    },
  },
});

export const {
  openPanel,
  closePanel,
  togglePanel,
  navigateTo,
  navigateBack,
  resetToHome,
} = blueticksPanelSlice.actions;

export default blueticksPanelSlice.reducer;
